import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, X, Send, Bot, User, Loader2, RotateCcw, Image as ImageIcon, Upload } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { geminiService } from '@/services/geminiService';

interface ImageData {
  id: string;
  url: string;
  name: string;
  type: string;
  base64: string;
}

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  image?: ImageData;
}

interface ChatHistory {
  id: number;
  user_id: number;
  conversation_id: string;
  message_type: string;
  message_content: string;
  timestamp: string;
  context_data: string;
}

export interface FloatingChatbotRef {
  openChat: () => void;
  openChatWithConversation: (conversationId: string) => void;
}

const FloatingChatbot = forwardRef<FloatingChatbotRef>((props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
  {
    id: '1',
    type: 'assistant',
    content: 'Hello! I\'m your AI teaching assistant. How can I help you today? You can also paste images directly into the chat for analysis and discussion.',
    timestamp: new Date()
  }]
  );
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(() => `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [pastedImage, setPastedImage] = useState<ImageData | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  useImperativeHandle(ref, () => ({
    openChat: () => {
      console.log('🚀 FloatingChatbot - Opening new chat');
      setIsOpen(true);
    },
    openChatWithConversation: (convId: string) => {
      console.log('🔄 FloatingChatbot - Opening with conversation:', convId);
      setConversationId(convId);
      setIsOpen(true);
      loadSpecificConversation(convId);
    }
  }));

  useEffect(() => {
    if (isOpen && user) {
      // Only load history if we're not already loading a specific conversation
      if (!isLoadingConversation) {
        loadChatHistory();
      }
    }
  }, [isOpen, user, conversationId]);

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // Add paste event listener for images
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!isOpen) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            handleImagePaste(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen]);

  const handleImagePaste = async (file: File) => {
    try {
      console.log('📸 Image pasted:', file.name, file.type);

      // Validate image file
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File",
          description: "Please paste a valid image file.",
          variant: "destructive"
        });
        return;
      }

      // Check file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: "File Too Large",
          description: "Please paste an image smaller than 5MB.",
          variant: "destructive"
        });
        return;
      }

      // Convert to base64
      const base64 = await convertFileToBase64(file);
      const imageUrl = URL.createObjectURL(file);

      const imageData: ImageData = {
        id: Date.now().toString(),
        url: imageUrl,
        name: file.name || 'pasted-image.png',
        type: file.type,
        base64: base64
      };

      setPastedImage(imageData);

      toast({
        title: "Image Ready",
        description: "Image pasted! Add a message and send to analyze."
      });

      // Focus on input after pasting
      if (inputRef.current) {
        inputRef.current.focus();
      }

    } catch (error) {
      console.error('Error handling pasted image:', error);
      toast({
        title: "Error",
        description: "Failed to process the pasted image.",
        variant: "destructive"
      });
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data:image/...;base64, prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const loadSpecificConversation = async (convId: string) => {
    if (!user) return;

    console.log('📥 Loading specific conversation:', convId);
    setIsLoadingConversation(true);

    try {
      const { data, error } = await window.ezsite.apis.tablePage('15991', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'timestamp',
        IsAsc: true,
        Filters: [
        {
          name: 'user_id',
          op: 'Equal',
          value: user.ID
        },
        {
          name: 'conversation_id',
          op: 'Equal',
          value: convId
        }]

      });

      if (error) {
        console.error('Failed to load specific conversation:', error);
        toast({
          title: "Error Loading Conversation",
          description: "Failed to load the conversation. Please try again.",
          variant: "destructive"
        });
        return;
      }

      const history: ChatHistory[] = data?.List || [];
      console.log('📋 Loaded conversation history:', history.length, 'messages');

      if (history.length > 0) {
        const loadedMessages: Message[] = history.map((h) => {
          const contextData = h.context_data ? JSON.parse(h.context_data) : {};
          return {
            id: h.id.toString(),
            type: h.message_type as 'user' | 'assistant',
            content: h.message_content,
            timestamp: new Date(h.timestamp),
            image: contextData.image || undefined
          };
        });

        setMessages(loadedMessages);

        toast({
          title: "Conversation Loaded",
          description: `Loaded ${loadedMessages.length} messages. You can continue chatting now.`
        });
      } else {
        console.log('⚠️ No history found for conversation:', convId);
        setMessages([{
          id: '1',
          type: 'assistant',
          content: 'I couldn\'t find any previous messages for this conversation. Let\'s start fresh! How can I help you?',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Error loading specific conversation:', error);
      toast({
        title: "Error Loading Conversation",
        description: "Failed to load the conversation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingConversation(false);
    }
  };

  const loadChatHistory = async () => {
    if (!user || isLoadingConversation) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage('15991', {
        PageNo: 1,
        PageSize: 50,
        OrderByField: 'timestamp',
        IsAsc: true,
        Filters: [
        {
          name: 'user_id',
          op: 'Equal',
          value: user.ID
        },
        {
          name: 'conversation_id',
          op: 'Equal',
          value: conversationId
        }]

      });

      if (error) {
        console.error('Failed to load chat history:', error);
        return;
      }

      const history: ChatHistory[] = data?.List || [];
      if (history.length > 0) {
        const loadedMessages: Message[] = history.map((h) => {
          const contextData = h.context_data ? JSON.parse(h.context_data) : {};
          return {
            id: h.id.toString(),
            type: h.message_type as 'user' | 'assistant',
            content: h.message_content,
            timestamp: new Date(h.timestamp),
            image: contextData.image || undefined
          };
        });

        setMessages((prev) => [prev[0], ...loadedMessages]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const saveChatMessage = async (message: Message) => {
    if (!user) return;

    try {
      const contextData = {
        userTier: 'basic',
        ...(message.image && { image: message.image })
      };

      const { error } = await window.ezsite.apis.tableCreate('15991', {
        user_id: user.ID,
        conversation_id: conversationId,
        message_type: message.type,
        message_content: message.content,
        timestamp: message.timestamp.toISOString(),
        context_data: JSON.stringify(contextData)
      });

      if (error) {
        console.error('Failed to save chat message:', error);
      } else {
        console.log('✅ Chat message saved successfully');
      }
    } catch (error) {
      console.error('Error saving chat message:', error);
    }
  };

  const buildConversationContext = () => {
    const recentMessages = messages.slice(1, -3);
    let contextString = '';

    if (recentMessages.length > 0) {
      contextString = 'Recent conversation:\n';
      recentMessages.forEach((msg) => {
        contextString += `${msg.type === 'user' ? 'User' : 'Assistant'}: ${msg.content}`;
        if (msg.image) {
          contextString += ' [Image shared]';
        }
        contextString += '\n';
      });
      contextString += '\n';
    }

    return contextString;
  };

  const analyzeImageWithGemini = async (imageData: ImageData, userMessage: string) => {
    try {
      const apiKey = (await (window.ezsite.gemini as any).getApiKey?.()) || (await getApiKey());

      if (!apiKey) {
        throw new Error('Gemini API key not configured');
      }

      const model = 'gemini-1.5-flash';
      const prompt = userMessage.trim() || 'Please analyze this image and describe what you see. If it appears to be educational content, explain how it could be used in teaching.';

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contents: [
            {
              parts: [
              {
                text: prompt
              },
              {
                inline_data: {
                  mime_type: imageData.type,
                  data: imageData.base64
                }
              }]

            }],

            generationConfig: {
              temperature: 0.7,
              maxOutputTokens: 1000,
              topP: 0.8,
              topK: 10
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Gemini Vision API Error:', errorData);
        throw new Error(`Gemini API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        return data.candidates[0].content.parts[0].text;
      } else {
        console.error('Unexpected response format:', data);
        throw new Error('Unexpected response format from Gemini Vision API');
      }

    } catch (error) {
      console.error('Error analyzing image with Gemini:', error);
      throw error;
    }
  };

  // Helper function to get API key (fallback)
  const getApiKey = async (): Promise<string> => {
    try {
      const response = await window.ezsite.apis.tablePage('15215', {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        {
          name: 'setting_key',
          op: 'Equal',
          value: 'gemini_api_key'
        }]
      });

      if (response.error || !response.data?.List || response.data.List.length === 0) {
        throw new Error('API key not found');
      }

      return response.data.List[0].setting_value.trim();
    } catch (error) {
      console.error('Error getting API key:', error);
      throw new Error('Failed to retrieve API key');
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() && !pastedImage || isLoading) return;

    const messageContent = inputMessage.trim() || (pastedImage ? 'Image shared' : '');

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageContent,
      timestamp: new Date(),
      image: pastedImage || undefined
    };

    setMessages((prev) => [...prev, userMessage]);
    saveChatMessage(userMessage);
    setInputMessage('');
    setPastedImage(null);
    setIsLoading(true);

    try {
      console.log('🤖 Calling AI for response...');
      let aiResponse;

      if (userMessage.image) {
        // Use Gemini Vision API for image analysis
        aiResponse = await analyzeImageWithGemini(userMessage.image, userMessage.content);
      } else {
        // Use regular text-based Gemini API
        const conversationContext = buildConversationContext();
        const { text, error } = await geminiService.getChatResponse(
          userMessage.content,
          conversationContext,
          'teacher'
        );

        if (error) {
          throw new Error(error);
        }

        aiResponse = text;
      }

      if (!aiResponse || aiResponse.trim() === '') {
        throw new Error('Empty response from AI service');
      }

      console.log('✅ Received response from AI');

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: aiResponse.trim(),
        timestamp: new Date()
      };

      setMessages((prev) => [...prev, aiMessage]);
      saveChatMessage(aiMessage);

    } catch (error) {
      console.error('❌ Error getting AI response:', error);

      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      let userFriendlyMessage = 'I apologize, but I\'m having trouble processing your request right now. ';

      if (errorMessage.includes('API key')) {
        userFriendlyMessage += 'It seems there\'s an issue with the AI configuration. Please contact your administrator.';
      } else if (errorMessage.includes('rate limit')) {
        userFriendlyMessage += 'The AI service is currently busy. Please try again in a few moments.';
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        userFriendlyMessage += 'Please check your internet connection and try again.';
      } else if (pastedImage) {
        userFriendlyMessage += 'There was an issue analyzing the image. Please try with a different image or text message.';
      } else {
        userFriendlyMessage += 'Please try rephrasing your question or try again later.';
      }

      const errorAiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: userFriendlyMessage,
        timestamp: new Date()
      };

      setMessages((prev) => [...prev, errorAiMessage]);

      toast({
        title: "AI Assistant Error",
        description: "There was an issue processing your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewConversation = () => {
    const newConversationId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setConversationId(newConversationId);
    setPastedImage(null);
    setMessages([{
      id: '1',
      type: 'assistant',
      content: 'Hello! I\'m your AI teaching assistant. How can I help you today? You can also paste images directly into the chat for analysis and discussion.',
      timestamp: new Date()
    }]);

    toast({
      title: "New Conversation",
      description: "Started a new conversation. Your previous chats are saved in history."
    });
  };

  const getConversationTitle = () => {
    if (messages.length > 1) {
      const firstUserMessage = messages.find((m) => m.type === 'user');
      if (firstUserMessage) {
        return firstUserMessage.content.length > 30 ?
        firstUserMessage.content.substring(0, 30) + '...' :
        firstUserMessage.content;
      }
    }
    return 'AI Teaching Assistant';
  };

  const removePastedImage = () => {
    if (pastedImage) {
      URL.revokeObjectURL(pastedImage.url);
      setPastedImage(null);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }} data-id="fm56c57wl" data-path="src/components/ai/FloatingChatbot.tsx">

        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-200"
          style={{ display: isOpen ? 'none' : 'flex' }} data-id="iqnogpsr4" data-path="src/components/ai/FloatingChatbot.tsx">

          <MessageCircle className="h-6 w-6 text-white" data-id="cd9eed5t2" data-path="src/components/ai/FloatingChatbot.tsx" />
        </Button>
      </motion.div>

      {/* Chat Window */}
      <AnimatePresence data-id="fgdct9bq0" data-path="src/components/ai/FloatingChatbot.tsx">
        {isOpen &&
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-6 right-6 z-50 w-96 h-[500px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]" data-id="p1juq9n6u" data-path="src/components/ai/FloatingChatbot.tsx">

            <Card className="h-full shadow-2xl border-0 bg-white/95 backdrop-blur-sm" data-id="v2em4b9hr" data-path="src/components/ai/FloatingChatbot.tsx">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg" data-id="oqcc6yfg1" data-path="src/components/ai/FloatingChatbot.tsx">
                <CardTitle className="text-lg font-semibold flex items-center gap-2" data-id="t0eg6g77l" data-path="src/components/ai/FloatingChatbot.tsx">
                  <Bot className="h-5 w-5" data-id="jlbtzd5qk" data-path="src/components/ai/FloatingChatbot.tsx" />
                  <span className="truncate" data-id="1ovqulck1" data-path="src/components/ai/FloatingChatbot.tsx">{getConversationTitle()}</span>
                </CardTitle>
                <div className="flex items-center gap-2" data-id="5w8z6qx3d" data-path="src/components/ai/FloatingChatbot.tsx">
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNewConversation}
                  className="text-white hover:bg-white/20 h-8 px-2 text-xs"
                  title="Start new conversation" data-id="59n5enn34" data-path="src/components/ai/FloatingChatbot.tsx">
                    <RotateCcw className="h-3 w-3" data-id="b201md7zg" data-path="src/components/ai/FloatingChatbot.tsx" />
                  </Button>
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0" data-id="j9p9kwx7i" data-path="src/components/ai/FloatingChatbot.tsx">

                    <X className="h-4 w-4" data-id="bxxkykh2z" data-path="src/components/ai/FloatingChatbot.tsx" />
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent className="flex flex-col h-[calc(100%-4rem)] p-0" data-id="vu4cytsqg" data-path="src/components/ai/FloatingChatbot.tsx">
                {/* Messages Area */}
                <ScrollArea className="flex-1 p-4" ref={scrollAreaRef} data-id="y4g6b8yln" data-path="src/components/ai/FloatingChatbot.tsx">
                  {isLoadingConversation ?
                <div className="flex items-center justify-center py-8" data-id="7pbh703k3" data-path="src/components/ai/FloatingChatbot.tsx">
                      <div className="text-center" data-id="pdloq5414" data-path="src/components/ai/FloatingChatbot.tsx">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-indigo-600" data-id="e88yy7doa" data-path="src/components/ai/FloatingChatbot.tsx" />
                        <p className="text-sm text-gray-600" data-id="k9hws0k5k" data-path="src/components/ai/FloatingChatbot.tsx">Loading conversation...</p>
                      </div>
                    </div> :

                <div className="space-y-4" data-id="bw0ndeq7j" data-path="src/components/ai/FloatingChatbot.tsx">
                      {messages.map((message) =>
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="z97i344hb" data-path="src/components/ai/FloatingChatbot.tsx">

                          <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 ${
                      message.type === 'user' ?
                      'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' :
                      'bg-gray-100 text-gray-800'}`
                      } data-id="kt97gc9on" data-path="src/components/ai/FloatingChatbot.tsx">

                            <div className="flex items-start gap-2" data-id="9qshsnzes" data-path="src/components/ai/FloatingChatbot.tsx">
                              {message.type === 'assistant' &&
                        <Bot className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="fh4lioepn" data-path="src/components/ai/FloatingChatbot.tsx" />
                        }
                              {message.type === 'user' &&
                        <User className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="b68j8mjsm" data-path="src/components/ai/FloatingChatbot.tsx" />
                        }
                              <div className="flex-1" data-id="u53xbsuup" data-path="src/components/ai/FloatingChatbot.tsx">
                                {message.image &&
                          <div className="mb-2" data-id="qus504ain" data-path="src/components/ai/FloatingChatbot.tsx">
                                    <img
                              src={message.image.url}
                              alt="Shared image"
                              className="max-w-full h-auto rounded border"
                              style={{ maxHeight: '200px' }} data-id="t5nnn7qwt" data-path="src/components/ai/FloatingChatbot.tsx" />

                                    <p className={`text-xs mt-1 ${
                            message.type === 'user' ? 'text-indigo-100' : 'text-gray-500'}`
                            } data-id="wb4lw5kqz" data-path="src/components/ai/FloatingChatbot.tsx">
                                      {message.image.name}
                                    </p>
                                  </div>
                          }
                                <p className="text-sm whitespace-pre-wrap" data-id="7hyxujlox" data-path="src/components/ai/FloatingChatbot.tsx">{message.content}</p>
                                <p className={`text-xs mt-1 ${
                          message.type === 'user' ? 'text-indigo-100' : 'text-gray-500'}`
                          } data-id="hofjmixd1" data-path="src/components/ai/FloatingChatbot.tsx">
                                  {message.timestamp.toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                  )}
                      
                      {isLoading &&
                  <div className="flex justify-start" data-id="2o2d3bmeg" data-path="src/components/ai/FloatingChatbot.tsx">
                          <div className="bg-gray-100 text-gray-800 rounded-lg px-3 py-2" data-id="b6j47sssb" data-path="src/components/ai/FloatingChatbot.tsx">
                            <div className="flex items-center gap-2" data-id="nt8rnbtwz" data-path="src/components/ai/FloatingChatbot.tsx">
                              <Bot className="h-4 w-4" data-id="9o5nmyke0" data-path="src/components/ai/FloatingChatbot.tsx" />
                              <Loader2 className="h-4 w-4 animate-spin" data-id="anvwo0zil" data-path="src/components/ai/FloatingChatbot.tsx" />
                              <span className="text-sm" data-id="xmd4lktup" data-path="src/components/ai/FloatingChatbot.tsx">AI is analyzing...</span>
                            </div>
                          </div>
                        </div>
                  }
                    </div>
                }
                </ScrollArea>

                {/* Input Area */}
                <div className="p-4 border-t bg-gray-50/50" data-id="3blmricp3" data-path="src/components/ai/FloatingChatbot.tsx">
                  {/* Pasted Image Preview */}
                  {pastedImage &&
                <div className="mb-3 relative" data-id="s1349dji9" data-path="src/components/ai/FloatingChatbot.tsx">
                      <div className="flex items-center gap-2 p-2 bg-white rounded-lg border" data-id="fvi7wtr1v" data-path="src/components/ai/FloatingChatbot.tsx">
                        <ImageIcon className="h-4 w-4 text-indigo-600" data-id="4oj9gxqc3" data-path="src/components/ai/FloatingChatbot.tsx" />
                        <img
                      src={pastedImage.url}
                      alt="Pasted preview"
                      className="h-12 w-12 object-cover rounded border" data-id="oud9arwqs" data-path="src/components/ai/FloatingChatbot.tsx" />

                        <div className="flex-1 min-w-0" data-id="ltyfv33ov" data-path="src/components/ai/FloatingChatbot.tsx">
                          <p className="text-sm font-medium truncate" data-id="ymp73ydla" data-path="src/components/ai/FloatingChatbot.tsx">{pastedImage.name}</p>
                          <p className="text-xs text-gray-500" data-id="jutunt24e" data-path="src/components/ai/FloatingChatbot.tsx">Ready to analyze</p>
                        </div>
                        <Button
                      variant="ghost"
                      size="sm"
                      onClick={removePastedImage}
                      className="h-8 w-8 p-0 text-gray-500 hover:text-red-500" data-id="pg0i4izs0" data-path="src/components/ai/FloatingChatbot.tsx">
                          <X className="h-4 w-4" data-id="6gs8a6szf" data-path="src/components/ai/FloatingChatbot.tsx" />
                        </Button>
                      </div>
                    </div>
                }

                  <div className="flex gap-2" data-id="uht2y4zwp" data-path="src/components/ai/FloatingChatbot.tsx">
                    <Input
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={pastedImage ? "Ask about the image..." : "Ask me anything or paste an image..."}
                    className="flex-1 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled={isLoading || isLoadingConversation} data-id="jne3ww7pv" data-path="src/components/ai/FloatingChatbot.tsx" />

                    <Button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() && !pastedImage || isLoading || isLoadingConversation}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white" data-id="jfw27wu5a" data-path="src/components/ai/FloatingChatbot.tsx">

                      <Send className="h-4 w-4" data-id="k90pcbkut" data-path="src/components/ai/FloatingChatbot.tsx" />
                    </Button>
                  </div>
                  
                  <div className="mt-2 flex items-center justify-between" data-id="72od3lfi0" data-path="src/components/ai/FloatingChatbot.tsx">
                    <div className="flex items-center gap-2" data-id="p3d2a7voy" data-path="src/components/ai/FloatingChatbot.tsx">
                      <Badge variant="secondary" className="text-xs" data-id="p9mxhd8s9" data-path="src/components/ai/FloatingChatbot.tsx">
                        Chat History Saved
                      </Badge>
                      {pastedImage &&
                    <Badge variant="outline" className="text-xs" data-id="1bgj6g767" data-path="src/components/ai/FloatingChatbot.tsx">
                          <ImageIcon className="h-3 w-3 mr-1" data-id="c5gefc73t" data-path="src/components/ai/FloatingChatbot.tsx" />
                          Image Ready
                        </Badge>
                    }
                    </div>
                    {user &&
                  <span className="text-xs text-gray-500" data-id="uwshi38ql" data-path="src/components/ai/FloatingChatbot.tsx">
                        Logged in as {user.Name}
                      </span>
                  }
                  </div>
                  
                  <div className="mt-1" data-id="n0drwmonc" data-path="src/components/ai/FloatingChatbot.tsx">
                    <p className="text-xs text-gray-400 text-center" data-id="64v9ijniv" data-path="src/components/ai/FloatingChatbot.tsx">
                      💡 Tip: Paste images directly (Ctrl+V) for AI analysis
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        }
      </AnimatePresence>
    </>);

});

FloatingChatbot.displayName = 'FloatingChatbot';

export default FloatingChatbot;